import { Fragment, useEffect, useRef, useState } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";

export interface IMultiSelectItem {
  id?: string;
  name: string;
  value: string | number | boolean | null;
}

interface IMultiSelectProps {
  list: IMultiSelectItem[];
  placeholder?: string;
  selected?: string[] | number[];
  onChange: (selected: any[]) => void;
  label?: string;
  required?: boolean;
  description?: string;
  error?: string;
  helperText?: string;
  selectInputClassName?: string;
  multiSelect?: boolean;
  onQueryChange?: (query: string) => void;
  loading?: boolean;
}

export default function MultiSelect(props: IMultiSelectProps) {
  const [selected, setSelected] = useState<IMultiSelectItem[]>(
    props.selected
      ? props.selected?.map(
        (value: any): IMultiSelectItem => ({ name: value, value: value })
      )
      : []
  );
  const [query, setQuery] = useState("");

  const {
    placeholder = "Select option",
    required = false,
    multiSelect = true,
  } = props;

  const filteredList =
    query === ""
      ? props.list
      : props.list.filter((listItem: IMultiSelectItem) =>
        listItem.name
          .toLowerCase()
          .replace(/\s+/g, "")
          .includes(query.toLowerCase().replace(/\s+/g, ""))
      );

  useEffect(() => {
    props.onChange(selected?.map((selectedItem: any) => selectedItem?.value));
  }, [selected]);

  useEffect(() => {
    props.onQueryChange?.(query);
  }, [query]);

  const isSelected = (listItem: IMultiSelectItem): boolean => {
    return (
      selected.find(
        (item: IMultiSelectItem) => item.value === listItem.value
      ) !== undefined
    );
  };

  const removeSelectedItem = (listItem: IMultiSelectItem) => {
    setSelected(
      selected.filter((item: IMultiSelectItem) => item.value !== listItem.value)
    );
  };

  return (
    <div className="">
      <Combobox
        value={selected}
        onChange={(value: any) => {
          if (multiSelect) {
            setSelected([...selected, value]);
          } else {
            setSelected([value]);
          }
        }}
      >
        {/* Label */}
        {props.label && (
          <Combobox.Label
            className={clsx("block text-sm font-medium text-gray-700 dark:text-text-dark-primary", {
              ["!text-danger-main"]: props.error && required,
            })}
          >
            {props.label}{" "}
            <span className="!text-danger-main">{required && "*"}</span>
            {props.description && (
              <p
                className={clsx("text-xs text-[#455A64] dark:text-text-dark-secondary italic", {
                  ["!text-danger-border"]: props.error && required,
                })}
              >
                {props.description}
              </p>
            )}
          </Combobox.Label>
        )}
        <div className="relative mt-1">
          <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white dark:bg-primary-dark-main dark:border-primary-dark-border_active text-left shadow-md focus-visible:ring-2 focus-visible:ring-white focus-visible:dark:ring-primary-dark-border_active focus-visible:ring-opacity-75 focus-visible:ring-offset-2 sm:text-sm border-[1px] border-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-main focus:dark:ring-primary-dark-border_active focus:border-primary-border focus:dark:border-primary-dark-border_active">
            <div className="pl-2 flex items-center flex-wrap space-y-1 py-1">
              {multiSelect &&
                selected.map((listItem: IMultiSelectItem, index: number) => (
                  <span
                    className="whitespace-nowrap bg-teal-600 text-white px-2 py-1 mr-2 rounded flex"
                    key={index}
                  >
                    {listItem.name}
                    <XMarkIcon
                      className="w-[16px] ml-2 cursor-pointer"
                      onClick={() => removeSelectedItem(listItem)}
                    />
                  </span>
                ))}
              <Combobox.Input
                className={clsx(
                  "border-none py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 dark:text-text-dark-primary focus:ring-0 placeholder:text-gray-400 placeholder:dark:text-text-dark-secondary w-full dark:bg-primary-dark-main dark:border-primary-dark-border_active focus:dark:border-primary-dark-border_active",
                  props.selectInputClassName
                )}
                displayValue={multiSelect ? () => "" : () => selected[0]?.name}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={placeholder}
                onClick={() => {
                  document.getElementById("comboInput")?.click();
                }}
              />
            </div>
            <Combobox.Button
              className="absolute inset-y-0 right-0 flex items-center pr-2"
              id="comboInput"
            >
              <ChevronUpDownIcon
                className="h-5 w-5 text-gray-400 dark:text-text-dark-secondary"
                aria-hidden="true"
              />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-primary-dark-main dark:border-primary-dark-border_active py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
              {filteredList?.length === 0 && query !== "" && !props.loading ? (
                <div className="relative cursor-default select-none py-2 px-4 text-gray-700 dark:text-text-dark-primary">
                  Nothing found.
                </div>
              ) : (
                filteredList.map(
                  (listItem: IMultiSelectItem) =>
                    !isSelected(listItem) && (
                      <Combobox.Option
                        key={listItem.id}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 hover:cursor-pointer ${active ? "bg-primary-surface dark:bg-primary-dark-secondary dark:text-text-dark-primary" : "text-gray-900 dark:text-text-dark-primary"
                          }`
                        }
                        value={listItem}
                      >
                        {({ active }) => (
                          <>
                            <span
                              className={`block truncate ${active ? "font-medium" : "font-normal"
                                }`}
                            >
                              {listItem.name}
                            </span>
                            {isSelected(listItem) && (
                              <span
                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-white" : "text-primary-main dark:text-text-dark-primary"
                                  }`}
                              >
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            )}
                          </>
                        )}
                      </Combobox.Option>
                    )
                )
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
      {/* Helper Text */}
      <div
        className={clsx("text-xs text-gray-400 dark:text-text-dark-secondary", {
          ["!text-danger-main"]: props.error,
        })}
      >
        {props.helperText}
      </div>
    </div>
  );
}
